import React, { useState, useEffect, useCallback } from 'react';
import { Ticket, ExternalLink, RefreshCw, AlertCircle, Bug, Monitor, Smartphone } from 'lucide-react';
import clsx from 'clsx';
import { apiService } from '../services/api';

/* Dashboard view of every Jira ticket this system has raised for a failing test.
   Top-level split: Web tests vs Mobile app. Within each, grouped by product
   (New Gear / Used Gear / Connect) → sub-category, with live Jira status. */

const PRODUCT_ORDER = ['New Gear', 'Used Gear', 'Connect'];

// Jira status (assignee + status) changes upstream; we sync every 30 min (and on
// demand). Show the operator when it last refreshed, in IST.
function formatIST(value) {
  if (!value) return null;
  const d = new Date(value);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true,
  }) + ' IST';
}

function StatusPill({ ticket }) {
  const cat = ticket.status_category || 'new';
  const theme = {
    new: 'bg-slate-100 text-slate-600',
    indeterminate: 'bg-amber-100 text-amber-700',
    done: 'bg-success-100 text-success-700',
  }[cat] || 'bg-slate-100 text-slate-600';
  return (
    <span className={clsx('inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium', theme)}>
      {ticket.jira_status || '—'}
    </span>
  );
}

function TicketRow({ t }) {
  const runHref = t.source === 'mobile' ? `/mobile-tests/${t.created_run_id}` : `/test-runs/${t.created_run_id}`;
  return (
    <li className="flex items-start justify-between gap-3">
      <div className="min-w-0 flex-1">
        <p className="text-sm text-gray-800 truncate" title={t.test_title}>{t.test_title}</p>
        <div className="flex flex-wrap items-center gap-2 mt-0.5 text-[11px] text-gray-400">
          {t.assignee && <span>Assignee: {t.assignee}</span>}
          {t.sprint && <span>· {t.sprint}</span>}
          {t.regression_count > 0 && <span className="text-amber-700">· regressed ×{t.regression_count}</span>}
          {t.created_run_id && (
            <a href={runHref} className="text-primary-600 hover:underline">· from run #{t.created_run_id}</a>
          )}
          {t.deleted_in_jira && (
            <span className="text-gray-400 italic" title="This issue no longer exists in Jira — kept here for reference, no longer synced">· deleted in Jira</span>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <StatusPill ticket={t} />
        <a
          href={t.url}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium rounded-md border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 transition-colors"
        >
          <span className="font-semibold">{t.jira_key}</span>
          <ExternalLink className="h-3 w-3 text-gray-400" />
        </a>
      </div>
    </li>
  );
}

function PlatformSection({ icon: Icon, label, tickets }) {
  if (!tickets.length) return null;

  // group by product → sub-category
  const groups = {};
  for (const t of tickets) {
    const product = t.product || 'Uncategorized';
    const sub = t.sub_category || t.area || 'General';
    groups[product] = groups[product] || {};
    groups[product][sub] = groups[product][sub] || [];
    groups[product][sub].push(t);
  }
  const orderedProducts = [
    ...PRODUCT_ORDER.filter((p) => groups[p]),
    ...Object.keys(groups).filter((p) => !PRODUCT_ORDER.includes(p)),
  ];

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 text-gray-500" />
        <h2 className="text-sm font-semibold text-gray-800">{label}</h2>
        <span className="text-xs text-gray-400">({tickets.length})</span>
      </div>
      {orderedProducts.map((product) => (
        <div key={product} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-5 py-3 border-b border-gray-200 bg-gray-50">
            <h3 className="text-sm font-semibold text-gray-800">{product}</h3>
          </div>
          <div className="divide-y divide-gray-100">
            {Object.entries(groups[product]).map(([sub, rows]) => (
              <div key={sub} className="px-5 py-3">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-2">{sub}</p>
                <ul className="space-y-2">
                  {rows.map((t) => <TicketRow key={t.id} t={t} />)}
                </ul>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

const JiraTickets = () => {
  const [tickets, setTickets] = useState([]);
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [lastSync, setLastSync] = useState(null);

  const load = useCallback(async () => {
    try {
      const [t, h] = await Promise.all([
        apiService.getJiraTickets(),
        apiService.getJiraHealth().catch(() => ({ data: { configured: false } })),
      ]);
      const rows = t.data || [];
      setTickets(rows);
      setHealth(h.data || null);
      // Last sync = most recent per-ticket status_synced_at across all tickets.
      const latest = rows.reduce((max, r) => {
        if (!r.status_synced_at) return max;
        const d = new Date(r.status_synced_at).getTime();
        return d > max ? d : max;
      }, 0);
      if (latest > 0) setLastSync(new Date(latest).toISOString());
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    }
  }, []);

  useEffect(() => {
    load().finally(() => setLoading(false));
  }, [load]);

  const handleSync = async () => {
    if (syncing) return;
    setSyncing(true);
    try {
      const res = await apiService.syncJiraTickets();
      if (res?.data?.synced_at) setLastSync(res.data.synced_at);
      await load();
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-600 border-t-transparent" />
      </div>
    );
  }

  const configured = health?.configured;
  const total = tickets.length;
  const openCount = tickets.filter((t) => t.is_open).length;

  // Distinct live statuses present, for the filter dropdown.
  const statusOptions = Array.from(new Set(tickets.map((t) => t.jira_status).filter(Boolean))).sort();
  const matchesFilter = (t) => {
    if (statusFilter === 'all') return true;
    if (statusFilter === '__open') return t.is_open;
    if (statusFilter === '__done') return !t.is_open;
    return t.jira_status === statusFilter;
  };
  const visible = tickets.filter(matchesFilter);
  const webTickets = visible.filter((t) => (t.source || 'web') !== 'mobile');
  const mobileTickets = visible.filter((t) => t.source === 'mobile');
  // Unfiltered totals for the header subtitle (independent of the active filter).
  const totalWeb = tickets.filter((t) => (t.source || 'web') !== 'mobile').length;
  const totalMobile = tickets.filter((t) => t.source === 'mobile').length;
  const lastSyncText = formatIST(lastSync);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary-600 flex items-center justify-center">
            <Ticket className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Jira Tickets</h1>
            <p className="text-sm text-gray-500">
              Tickets raised from failing tests · {total} total, {openCount} open
              {' · '}{totalWeb} web, {totalMobile} mobile
            </p>
          </div>
        </div>
        <div className="flex flex-col items-start sm:items-end gap-1">
          <button
            onClick={handleSync}
            disabled={syncing || !configured}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 transition-colors self-start"
          >
            <RefreshCw className={clsx('h-4 w-4', syncing && 'animate-spin')} />
            {syncing ? 'Syncing…' : 'Sync status'}
          </button>
          <span className="text-[11px] text-gray-400">
            {lastSyncText ? `Last synced: ${lastSyncText}` : 'Not synced yet'}
          </span>
        </div>
      </div>

      {/* Status filter */}
      {configured && total > 0 && (
        <div className="flex items-center gap-2">
          <label htmlFor="jira-status-filter" className="text-xs font-medium text-gray-500">Filter by status</label>
          <select
            id="jira-status-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="rounded-lg border border-gray-300 px-2.5 py-1.5 text-sm text-gray-700 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
          >
            <option value="all">All statuses ({total})</option>
            <option value="__open">Open — not Done ({openCount})</option>
            <option value="__done">Done ({total - openCount})</option>
            {statusOptions.length > 0 && <option disabled>──────────</option>}
            {statusOptions.map((s) => (
              <option key={s} value={s}>{s} ({tickets.filter((t) => t.jira_status === s).length})</option>
            ))}
          </select>
          {statusFilter !== 'all' && (
            <span className="text-xs text-gray-400">{visible.length} shown</span>
          )}
        </div>
      )}

      {/* Not configured banner */}
      {!configured && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="text-sm text-amber-800">
            <p className="font-medium">Jira is not configured on the server.</p>
            <p className="mt-0.5 text-amber-700">
              Set <code className="text-xs">JIRA_API_TOKEN</code> (and the other <code className="text-xs">JIRA_*</code> vars) in the backend environment to enable ticket creation.
            </p>
          </div>
        </div>
      )}

      {error && <div className="text-center py-8 text-gray-600">{error}</div>}

      {/* Empty */}
      {configured && total === 0 && !error && (
        <div className="bg-white border border-gray-200 rounded-xl p-10 text-center">
          <Bug className="h-8 w-8 text-gray-300 mx-auto mb-3" />
          <p className="text-sm font-medium text-gray-700">No tickets raised yet</p>
          <p className="text-xs text-gray-500 mt-1">
            Open a failed test run and click “Create Jira ticket” on a failure to file one here.
          </p>
        </div>
      )}

      {configured && total > 0 && visible.length === 0 && (
        <div className="bg-white border border-gray-200 rounded-xl p-8 text-center text-sm text-gray-500">
          No tickets match this status filter.
        </div>
      )}

      <PlatformSection icon={Monitor} label="Web tests" tickets={webTickets} />
      <PlatformSection icon={Smartphone} label="Mobile app" tickets={mobileTickets} />
    </div>
  );
};

export default JiraTickets;
